<template>
    <div>
        <div class="flex justify-between items-center mb-4">
            <h2 class="text-lg font-semibold text-gray-700">Assignments</h2>
            <button 
                @click="exportToExcel" 
                class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                title="Download as CSV (Excel compatible)"
            >
                <svg class="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download CSV
            </button>
        </div>

        <div class="overflow-x-auto border border-gray-200 rounded-lg shadow-sm">
            <table class="min-w-full divide-y divide-gray-200 text-xs lg:text-sm">
                <thead class="bg-gray-50">
                    <tr>
                        <th 
                            v-for="column in activeColumns" 
                            :key="column.key"
                            @click="sortBy(column.key)"
                            scope="col" 
                            class="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                        >
                            <div class="flex items-center">
                                {{ column.label }}
                                <span v-if="getSortHistoryIndex(column.key) !== -1" class="ml-1 flex items-center group">
                                    <svg v-if="getSortOrder(column.key) === 'asc'" class="w-3 h-3" :class="getSortHistoryIndex(column.key) === 0 ? 'text-blue-600' : 'text-gray-400'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
                                    </svg>
                                    <svg v-else class="w-3 h-3" :class="getSortHistoryIndex(column.key) === 0 ? 'text-blue-600' : 'text-gray-400'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                                    </svg>
                                    <span v-if="sortHistory.length > 1" class="text-[8px] font-bold mr-1" :class="getSortHistoryIndex(column.key) === 0 ? 'text-blue-600' : 'text-gray-400'">
                                        {{ getSortHistoryIndex(column.key) + 1 }}
                                    </span>
                                    <button 
                                        @click.stop="removeSort(column.key)"
                                        class="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-500 transition-all p-0.5 rounded-full hover:bg-red-200"
                                        title="Remove sort"
                                    >
                                        <svg class="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </span>
                            </div>
                        </th>
                    </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                    <tr v-for="assignment in sortedAssignments" :key="assignment.id" class="hover:bg-gray-50 transition-colors duration-150">
                        <td class="px-4 py-2 whitespace-nowrap font-medium text-gray-900">
                            {{ assignment.name }}
                        </td>
                        <td class="px-4 py-2 whitespace-nowrap text-gray-500">
                            {{ assignment.date }}
                        </td>
                        <td class="px-4 py-2 whitespace-nowrap text-gray-500">
                            {{ assignment.document_type }}
                        </td>
                        <td class="px-4 py-2 whitespace-nowrap text-gray-500">
                            {{ assignment.submitted_at }}
                        </td>
                        <td class="px-4 py-2 whitespace-nowrap">
                            <span v-if="assignment.completed_at" 
                                  class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-800">
                                Completed {{ assignment.completed_at }}
                            </span>
                            <span v-else-if="assignment.problem" 
                                  class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-100 text-red-800">
                                Problem
                            </span>
                            <span v-else 
                                  class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-yellow-100 text-yellow-800">
                                {{ getStatusLabel(assignment) }}
                            </span>
                        </td>
                        <td v-if="hasProblems" class="px-4 py-2 text-red-600 italic truncate max-w-[200px]" :title="assignment.problem_comment">
                            {{ assignment.problem ? (assignment.problem_comment || 'Details not provided') : '' }}
                        </td>
                        <td v-if="hasComments" class="px-4 py-2 text-gray-500 truncate max-w-[200px]" :title="assignment.comments">
                            {{ assignment.comments }}
                        </td>
                    </tr>
                    <tr v-if="!sortedAssignments.length">
                        <td :colspan="activeColumns.length" class="px-4 py-8 text-center text-gray-500">
                            No assignments found.
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import moment from 'moment';

export default {
    props: {
        agency: {
            type: Object,
            required: true
        },
        initialAssignments: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            assignments: this.initialAssignments,
            sortHistory: [
                { key: 'completed_at', order: 'desc' },
                { key: 'name', order: 'asc' }
            ],
        }
    },
    computed: {
        hasComments() {
            return this.assignments.some(a => a.comments && a.comments.trim().length > 0);
        },
        hasProblems() {
            return this.assignments.some(a => a.problem);
        },
        activeColumns() {
            const cols = [
                { label: 'Name', key: 'name' },
                { label: 'Doc Date', key: 'date' },
                { label: 'Type', key: 'document_type' },
                { label: 'Submitted', key: 'submitted_at' },
                { label: 'Status / Completed', key: 'completed_at' }
            ];
            if (this.hasProblems) {
                cols.push({ label: 'Problem Detail', key: 'problem_comment' });
            }
            if (this.hasComments) {
                cols.push({ label: 'Comments', key: 'comments' });
            }
            return cols;
        },
        primarySort() {
            return this.sortHistory[0] || { key: 'name', order: 'asc' };
        },
        sortedAssignments() {
            return [...this.assignments].sort((a, b) => {
                for (let sort of this.sortHistory) {
                    let valA = this.getSortValue(a, sort.key);
                    let valB = this.getSortValue(b, sort.key);

                    if (valA === valB) continue;
                    
                    let order = sort.order === 'asc' ? 1 : -1;
                    return valA > valB ? order : -order;
                }
                return 0;
            });
        }
    },
    methods: {
        getSortHistoryIndex(key) {
            return this.sortHistory.findIndex(s => s.key === key);
        },
        getSortOrder(key) {
            const sort = this.sortHistory.find(s => s.key === key);
            return sort ? sort.order : null;
        },
        removeSort(key) {
            const index = this.sortHistory.findIndex(s => s.key === key);
            if (index !== -1) {
                this.sortHistory.splice(index, 1);
            }
        },
        sortBy(key) {
            const index = this.sortHistory.findIndex(s => s.key === key);
            let order = 'asc';

            if (index === 0) {
                // Toggle order of current primary sort
                order = this.sortHistory[0].order === 'asc' ? 'desc' : 'asc';
                this.sortHistory[0].order = order;
            } else {
                // Make this the primary sort
                if (index !== -1) {
                    order = this.sortHistory[index].order;
                    this.sortHistory.splice(index, 1);
                } else if (key === 'completed_at' || key === 'date' || key === 'submitted_at' || key === 'created_at') {
                    // Default to desc for date/status columns
                    order = 'desc';
                }
                this.sortHistory.unshift({ key, order });
            }

            // Limit history to 3 levels for simplicity/performance
            if (this.sortHistory.length > 3) {
                this.sortHistory = this.sortHistory.slice(0, 3);
            }
        },
        getSortValue(item, key) {
            if (key === 'date') return item.raw_date || '0';
            if (key === 'submitted_at') return item.raw_submitted_at ? item.raw_submitted_at.substring(0, 10) : '0';
            if (key === 'completed_at') {
                if (item.completed_at) return 'z_' + (item.raw_completed_at ? item.raw_completed_at.substring(0, 10) : '0'); // Ignore time for sorting
                if (item.problem) return 'c_problem';
                if (item.started_at) return 'b_started';
                if (item.submitted_at !== '-') return 'a_not_started';
                return '0_draft';
            }
            const val = item[key];
            return val ? val.toString().toLowerCase() : '';
        },
        getStatusLabel(assignment) {
            if (assignment.problem) return 'Problem';
            if (assignment.started_at) return 'Started';
            if (assignment.submitted_at !== '-') return 'Not Started';
            return 'Draft';
        },
        exportToExcel() {
            // Define all possible headers
            const columnDefinitions = [
                { label: 'Name', getValue: a => a.name },
                { label: 'Document Date', getValue: a => a.date },
                { label: 'Type', getValue: a => a.document_type },
                { label: 'Submitted Date', getValue: a => a.submitted_at },
                { 
                    label: 'Status / Completed', 
                    getValue: a => {
                        if (a.completed_at) return 'Completed ' + a.completed_at;
                        return this.getStatusLabel(a);
                    }
                }
            ];

            // Add conditional columns
            if (this.hasProblems) {
                columnDefinitions.push({ 
                    label: 'Problem Detail', 
                    getValue: a => a.problem ? (a.problem_comment || 'Details not provided') : '' 
                });
            }

            if (this.hasComments) {
                columnDefinitions.push({ 
                    label: 'Comments', 
                    getValue: a => a.comments || '' 
                });
            }

            const headers = columnDefinitions.map(col => col.label);
            const rows = this.sortedAssignments.map(a => {
                return columnDefinitions.map(col => {
                    const val = col.getValue(a);
                    // Standard CSV escaping: quote everything, escape internal quotes
                    const content = (val || '').toString().replace(/"/g, '""').replace(/\s+/g, ' ').trim();
                    return `"${content}"`;
                });
            });

            const csvData = [
                headers.map(h => `"${h}"`).join(','),
                ...rows.map(row => row.join(','))
            ].join('\n');

            const blob = new Blob(['\uFEFF' + csvData], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement("a");
            const url = URL.createObjectURL(blob);
            const fileName = 'Assignment History (' + moment().format('YYYYMMDD') + ').csv';
            
            link.setAttribute("href", url);
            link.setAttribute("download", fileName);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }
    }
}
</script>

<style scoped>
th {
    user-select: none;
}
</style>
